import { useLocation, Link } from '@solidjs/router';
import { Show, createEffect, createSignal, useContext } from 'solid-js';
import clsx from 'clsx';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import { ImageComponent } from '../image-component/image-component';
import { ArrowRightIcon } from '../icons-library/arrow-right';
import { HeartCviIcon } from '../icons-library/heart-cvi';
import { HeartCviFilledIcon } from '../icons-library/heart-cvi-filled';
import { addMyContent, deleteMyContentItemByPermalink, getMyContent, MyContentItem } from '../my-content/my-content-helpers';
import { EventListCardData } from '../event/event-types';
import { isPastEvent } from './helpers/is-past-event';
import { getEventEyebrow } from './helpers/event-card-overrides';
import { removeTrailingSlash } from '../../tools/remove-trailing-slash';
import decodeHtmlEntities from '../../tools/decode-html-entities';

/* The list-view row from the events design: thumbnail column, eyebrow + title, and the
   heart / arrow icon buttons bottom-right. The whole row is the link; the arrow is
   decorative, the heart is a real button layered above the link. Rows are separated by a
   hairline and dim when the event is past, like `stale` on the gallery card. */
const ICON_BUTTON = 'icon-button-secondary-neutral icon-button-secondary-neutral-sm';

/* 29/16 and a 4-of-12 track are the gallery card's box, so a row's thumbnail is the same
   size as a gallery card's image and the two views line up when you toggle between them. */
const THUMBNAIL_RATIO = 29 / 16;
const THUMBNAIL_WIDTH = 750;

export const ListViewEventCard = (props: { event: EventListCardData }) => {
    const { localize, userState, pageRegistration, siteInfo } = useContext(AppContext);
    const [isSaved, setIsSaved] = createSignal(false);

    const isHiddenForPublic = () => {
        return props.event?.isHealthcareProfessional
            && !props.event?.isVisibleForPublic
            && !userState.user
            && pageRegistration.url !== '';
    };

    const myContentEnabled = () => siteInfo.siteType === 'default';

    createEffect(() => {
        const permalink = props.event?.permalink;
        if (!permalink) {
            setIsSaved(false);
            return;
        }
        const existing = (getMyContent() as MyContentItem[]).find((item) => item.permalink === permalink);
        setIsSaved(Boolean(existing));
    });

    const toggleSaved = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const permalink = props.event.permalink || '/';
        if (isSaved()) {
            deleteMyContentItemByPermalink(permalink);
            setIsSaved(false);
            return;
        }
        addMyContent({
            title: props.event.listCardContent?.title ?? '',
            permalink,
            type: 'event',
        });
        setIsSaved(true);
    };

    const image = () => props.event?.listCardContent?.image;
    /* WordPress hands us `altText` on some post types and `alt` on others. */
    const altText = () => image()?.altText || image()?.alt || '';
    const title = () => decodeHtmlEntities(props.event?.listCardContent?.title ?? '');
    const eyebrow = () => getEventEyebrow(props.event, localize, siteInfo.key);

    return (
        <li class="border-b border-cvi-border-default">
            <Link
                href={isHiddenForPublic() ? useLocation().pathname : removeTrailingSlash(props.event?.url)}
                class={clsx(
                    'group grid grid-cols-1 gap-cvi-6 py-cvi-6 no-underline text-cvi-fg-default',
                    'tablet:grid-cols-12 tablet:gap-x-[var(--block-grid-gap)]',
                    isHiddenForPublic() && 'pointer-events-none',
                    isPastEvent(props.event) && 'opacity-60',
                )}
            >
                <div class="relative aspect-[29/16] w-full shrink-0 overflow-hidden rounded-cvi-md bg-cvi-bg-neutral-subtle tablet:col-span-4">
                    <Show when={image()?.url}>
                        <div class="absolute inset-0">
                            <ImageComponent
                                src={image()!.url}
                                height={'AUTO'}
                                width={THUMBNAIL_WIDTH}
                                ratio={THUMBNAIL_RATIO}
                                fit="cover"
                                altText={altText()}
                                roundCorners={false}
                                spacer={false}
                            />
                        </div>
                    </Show>
                </div>

                <div class="relative flex min-w-0 flex-col gap-cvi-2 pb-cvi-11 tablet:col-span-8 tablet:pb-0 tablet:pr-cvi-13">
                    <Show when={eyebrow()}>
                        <span class="body-xs font-cvi-regular text-cvi-fg-muted">{eyebrow()}</span>
                    </Show>

                    <h3 class="m-0 heading-xl font-cvi-medium text-cvi-fg-default">
                        {title()}
                    </h3>

                    <Show when={isHiddenForPublic()}>
                        <div class="mt-cvi-2">
                            <LockedContent isColumn={false} />
                        </div>
                    </Show>

                    <div class="absolute bottom-0 right-0 flex items-center gap-cvi-3">
                        <Show when={myContentEnabled()}>
                            <button
                                type="button"
                                class={clsx(ICON_BUTTON, 'relative z-20 pointer-events-auto')}
                                aria-label={
                                    isSaved() ? localize('my_content_remove_star', 'Remove from My Content') : localize('save-to-my-content', 'Save to my content')
                                }
                                aria-pressed={isSaved()}
                                onClick={toggleSaved}
                            >
                                <Show when={isSaved()} fallback={<HeartCviIcon size="small" />}>
                                    <HeartCviFilledIcon size="small" />
                                </Show>
                            </button>
                        </Show>
                        <span aria-hidden="true" class={ICON_BUTTON}>
                            <ArrowRightIcon size="small" />
                        </span>
                    </div>
                </div>
            </Link>
        </li>
    );
};
